import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatRomeDateTime } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Bell, Check, CheckCheck, Ticket, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationCenter = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!user?.id) return;
    
    try {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/ws/${user.id}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        
        try {
          const notification = JSON.parse(event.data);
          setNotifications(prev => [{
            id: Date.now().toString(),
            type: notification.type,
            message: getNotificationMessage(notification),
            data: notification,
            read: false,
            created_at: new Date().toISOString()
          }, ...prev]);
          setUnreadCount(prev => prev + 1);
        } catch (err) {
          // Non-critical: ignore parse errors
        }
      };
      
      ws.onclose = () => {
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        // Reconnect after 10 seconds, max 3 attempts
        if (!ws.manualClose && ws.reconnectAttempts < 3) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 10000);
        }
      };
      
      ws.onerror = () => {
        // WebSocket errors are non-critical - fallback to polling
        ws.reconnectAttempts = (ws.reconnectAttempts || 0) + 1;
      };
      
      ws.reconnectAttempts = 0;
      wsRef.current = ws;
    } catch (e) {
      // WebSocket not available - use polling only
      console.log('WebSocket not available, using polling');
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
    connectWebSocket();
    
    // Polling fallback every 30 seconds
    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => {
      if (wsRef.current) {
        wsRef.current.manualClose = true;
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      clearInterval(pollInterval);
    };
  }, [fetchNotifications, connectWebSocket]);

  const getNotificationMessage = (notification) => {
    switch (notification.type) {
      case 'new_ticket':
        return `Nuovo ticket: ${notification.title}`;
      case 'ticket_update':
        return `Ticket aggiornato: ${notification.title} - ${notification.action}`;
      case 'ticket_assigned':
        return `Ti è stato assegnato: ${notification.title}`;
      default:
        return notification.message || 'Nuova notifica';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_ticket':
        return <Ticket className="w-4 h-4 text-blue-600" />;
      case 'ticket_update':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'ticket_assigned':
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      default:
        return <Bell className="w-4 h-4 text-slate-600" />;
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="h-5 w-5 text-slate-600" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifiche</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs"
              onClick={markAllAsRead}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Segna tutte lette
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-72">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              Nessuna notifica
            </div>
          ) : (
            notifications.slice(0, 20).map((notification) => (
              <DropdownMenuItem 
                key={notification.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${!notification.read ? 'bg-blue-50' : ''}`}
                onClick={() => markAsRead(notification.id)}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                    {notification.message || getNotificationMessage(notification.data || notification)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatRomeDateTime(notification.created_at)}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;
